import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { PdialComplianceView } from "@vd/api-client";
import { modeNotice, ParallelDialModes, PhoneNumber, Table, TBody, Td, THead, Th, Tr } from "@vd/ui";
import { useState } from "react";
import { errorMessage } from "../../../api/client";
import {
  attemptsQuery,
  complianceQuery,
  learnQuery,
  modesQuery,
  type PdialAdmin,
  type PdialPolicy,
  pct,
  pdial,
  pdialKeys,
  pdialModes,
  pdialQuery,
  requiredItems,
  type SettingsPatch,
  type StateRule,
  type TextCheckView,
} from "../../../api/pdial";
import { requireAdmin } from "../../../auth/useMe";
import { Acknowledge, LearnContent } from "../../../components/pdial/LearnAcknowledge";
import { availabilityBadge, CompanyLimits, Meters, RepLimits } from "../../../components/pdial/Limits";
import { PhoneTypesCard } from "../../../components/pdial/PhoneTypesCard";
import { TextCheck } from "../../../components/pdial/TextCheck";
import { toast } from "../../../components/toast";
import {
  Badge,
  Button,
  Card,
  Dialog,
  ErrorState,
  HelpTip,
  Input,
  Loading,
  PageHeader,
  Switch,
  Tabs,
} from "../../../components/ui";
import { fmtDateTime, fmtPhone } from "../../../lib/format";

export const Route = createFileRoute("/admin/calling/parallel-dial")({
  staticData: { crumb: { label: "Parallel dialing", parents: [] } },
  beforeLoad: requireAdmin,
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.prefetchQuery(pdialQuery),
      context.queryClient.prefetchQuery(complianceQuery),
      context.queryClient.prefetchQuery(modesQuery),
      context.queryClient.prefetchQuery(learnQuery),
    ]),
  component: ParallelDialingPage,
});

type Tab = "mode" | "setup" | "limits" | "rules" | "records";

function ParallelDialingPage() {
  const q = useQuery(pdialQuery);
  const c = useQuery(complianceQuery);
  const [tab, setTab] = useState<Tab>("mode");
  return (
    <>
      <PageHeader title="Parallel dialing" />
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : (
        <>
          <Banners s={q.data} c={c.data} />
          <Tabs<Tab>
            label="Parallel dialing sections"
            value={tab}
            onChange={setTab}
            tabs={[
              { value: "mode", label: "Mode" },
              { value: "setup", label: "Setup" },
              { value: "limits", label: "Reps" },
              { value: "rules", label: "Rules" },
              { value: "records", label: "Records" },
            ]}
          />
          {tab === "mode" ? <ModeTab /> : null}
          {tab === "setup" ? <Setup s={q.data} /> : null}
          {tab === "limits" ? <LimitsTab s={q.data} /> : null}
          {tab === "rules" ? <RulesTab s={q.data} /> : null}
          {tab === "records" ? <RecordsTab s={q.data} /> : null}
        </>
      )}
    </>
  );
}

function InlineError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p role="alert" className="text-caption text-danger-ink">
      {errorMessage(error)}
    </p>
  );
}

function useSave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: SettingsPatch) => pdial.putSettings(p),
    onSuccess: (v) => {
      qc.setQueryData(pdialKeys.admin, v);
      toast("Parallel dialing settings saved.");
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });
}

function Banners({ s, c }: { s: PdialAdmin; c: PdialComplianceView | undefined }) {
  const notes: { tone: "warn" | "danger"; text: string }[] = [];
  const mode = c ? modeNotice(c) : null;
  if (mode) notes.push({ tone: "danger", text: mode });
  if (s.needs_reacknowledgment)
    notes.push({
      tone: "danger",
      text: "The parallel dialing instructions changed. Reps dial one line at a time until an admin reads them and agrees again.",
    });
  for (const m of s.setup_missing) notes.push({ tone: "warn", text: `Needed: ${m}` });
  if (s.migration_notice) notes.push({ tone: "danger", text: s.migration_notice });
  if (notes.length === 0) return null;
  return (
    <div className="mb-4 flex flex-col gap-2">
      {notes.map((n) => (
        <div
          key={n.text}
          role="status"
          className={`rounded-md px-3 py-2 text-body ${n.tone === "danger" ? "bg-danger-soft text-danger-ink" : "bg-warning-soft text-ink"}`}
        >
          {n.text}
        </div>
      ))}
    </div>
  );
}

function Row({ label, help, children }: { label: string; help?: string; children: React.ReactNode }) {
  return (
    <li className="grid items-start gap-x-4 gap-y-2 px-4 py-3 md:grid-cols-[minmax(0,240px)_minmax(0,1fr)]">
      <div className="flex items-center gap-1.5 pt-1">
        <span className="font-medium text-ui">{label}</span>
        {help ? <HelpTip text={help} /> : null}
      </div>
      <div className="min-w-0">{children}</div>
    </li>
  );
}

// Mode ----------------------------------------------------------------------------------------

function ModeTab() {
  const c = useQuery(complianceQuery);
  const m = useQuery(modesQuery);
  const qc = useQueryClient();
  // Every write returns the whole view; the company section reads the mode too.
  const apply = async (p: Promise<PdialComplianceView>) => {
    const v = await p;
    qc.setQueryData(pdialKeys.compliance, v);
    void qc.invalidateQueries({ queryKey: pdialKeys.admin, exact: true });
  };
  if (c.isPending || m.isPending) return <Loading />;
  if (c.isError) return <ErrorState error={c.error} onRetry={() => c.refetch()} />;
  if (m.isError) return <ErrorState error={m.error} onRetry={() => m.refetch()} />;
  return (
    <ParallelDialModes
      className="max-w-[880px]"
      view={c.data}
      modes={m.data}
      acknowledge={(mode, b) => apply(pdialModes.acknowledge(mode, b))}
      savePatch={(p) => apply(pdialModes.save(p))}
      createOffering={(b) => apply(pdialModes.createOffering(b))}
      patchOffering={(id, p) => apply(pdialModes.patchOffering(id, p))}
      attest={(id, b) => apply(pdialModes.attest(id, b))}
      revoke={(id) => apply(pdialModes.revoke(id))}
      setCampaignOffering={(cid, oid) => apply(pdialModes.setCampaignOffering(cid, oid))}
      draftMessage={(b) => apply(pdialModes.draftMessage(b))}
      editMessage={(id, e) => apply(pdialModes.editMessage(id, e))}
      approveMessage={(id) => apply(pdialModes.approveMessage(id))}
    />
  );
}

// Setup ---------------------------------------------------------------------------------------

function Setup({ s }: { s: PdialAdmin }) {
  const save = useSave();
  const missing = s.required_items.filter((i) => !s.acked_items.includes(i));
  return (
    <div className="flex flex-col gap-4">
      <Card title="Parallel dialing">
        <ul className="divide-y divide-hairline">
          <Row label="Turn on for the company" help="Then allow it for each rep on Reps and limits.">
            <div className="flex flex-wrap items-center gap-3">
              <Switch
                label="Parallel dialing"
                checked={s.enabled}
                disabled={save.isPending || (!s.enabled && missing.length > 0)}
                onChange={(v) => save.mutate({ enabled: v })}
              />
              <InlineError error={save.error} />
              <span className="text-ui text-ink-2">
                {s.enabled
                  ? `On since ${s.enabled_at ? fmtDateTime(s.enabled_at) : "now"}`
                  : missing.length > 0
                    ? "Read the instructions and agree first."
                    : "Off"}
              </span>
            </div>
          </Row>
        </ul>
      </Card>
      <LearnCard s={s} />
      <SecondAnswerCard
        key={`${s.second_answer_mode}-${s.voip_as_cell}-${s.callback_assistant}-${s.callback_instructions}`}
        s={s}
      />
      <PhoneTypesCard />
    </div>
  );
}

function LearnCard({ s }: { s: PdialAdmin }) {
  const learn = useQuery(learnQuery);
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ack = useMutation({
    mutationFn: (items: string[]) => pdial.acknowledge(s.content_version, items),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pdialKeys.admin });
      setOpen(false);
      toast("Agreement recorded.");
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  const done = s.required_items.every((i) => s.acked_items.includes(i));
  return (
    <Card
      title="Learn how Parallel Dialing Works"
      actions={
        <Button variant={done ? "secondary" : "tinted"} onClick={() => setOpen(true)}>
          {done ? "Read again" : "Read and agree"}
        </Button>
      }
    >
      <div className="flex flex-col gap-2 px-4 py-3 text-body">
        {s.acknowledgment && done ? (
          <p>
            <Badge tone="ok">Agreed</Badge> by {s.acknowledgment.user_name ?? "an admin"} on{" "}
            {fmtDateTime(s.acknowledgment.acknowledged_at)}, version{" "}
            <span className="tnum">{s.content_version}</span>.
          </p>
        ) : (
          <p className="text-ink-2">
            Power, parallel and predictive dialing, first answer wins, abandoned calls and the 3% limit, the
            second-answer choices, cell phones, state rules, do not call, caller ID, records, and the open
            legal questions. Each risk {s.company} accepts is its own box.
          </p>
        )}
      </div>
      <Dialog open={open} onClose={() => setOpen(false)} title="Learn how Parallel Dialing Works" size="lg">
        {learn.isPending ? (
          <Loading />
        ) : learn.isError ? (
          <ErrorState error={learn.error} onRetry={() => learn.refetch()} />
        ) : (
          <div className="flex max-h-[70vh] flex-col gap-6 overflow-y-auto pr-1">
            <LearnContent learn={learn.data} />
            <Acknowledge
              learn={learn.data}
              mode={s.second_answer_mode}
              assistant={s.callback_assistant}
              cells={s.cells_in_batches}
              acked={s.acked_items}
              busy={ack.isPending}
              onConfirm={(items) => ack.mutate(items)}
            />
          </div>
        )}
      </Dialog>
    </Card>
  );
}

function SecondAnswerCard({ s }: { s: PdialAdmin }) {
  const save = useSave();
  const learn = useQuery(learnQuery);
  const qc = useQueryClient();
  const [ackOpen, setAckOpen] = useState(false);
  const [instructions, setInstructions] = useState(s.callback_instructions ?? "");
  const [assistant, setAssistant] = useState(s.callback_assistant);
  const [voip, setVoip] = useState(s.voip_as_cell);
  const [insCheck, setInsCheck] = useState<TextCheckView | null>(s.last_instructions_check);
  // What a second answer hears follows the mode (the Mode tab); the assistant goes with the
  // identification message.
  const message = s.second_answer_mode === "message";
  const patch: SettingsPatch = {};
  if ((instructions.trim() || null) !== (s.callback_instructions ?? null))
    patch.callback_instructions = instructions.trim() || null;
  if (assistant !== s.callback_assistant) patch.callback_assistant = assistant;
  if (voip !== s.voip_as_cell) patch.voip_as_cell = voip;
  const dirty = Object.keys(patch).length > 0;
  // Items the new settings need that nobody accepted yet: saving asks for them first, and the
  // company stays on.
  const needed = learn.data
    ? requiredItems(learn.data.content.acknowledgments, s.second_answer_mode, assistant, s.cells_in_batches)
        .map((i) => i.id)
        .filter((id) => !s.acked_items.includes(id))
    : [];
  const ackThenSave = useMutation({
    mutationFn: async (items: string[]) => {
      await pdial.acknowledge(s.content_version, items);
      return pdial.putSettings(patch);
    },
    onSuccess: (v) => {
      qc.setQueryData(pdialKeys.admin, v);
      setAckOpen(false);
      toast("Agreement recorded and settings saved.");
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  return (
    <Card title="When a second person answers">
      <ul className="divide-y divide-hairline">
        <Row
          label="Team overflow"
          help="A free teammate on the same campaign takes the second person first. That is a conversation, not an abandoned call."
        >
          <span className="text-body text-ink-2">Always on</span>
        </Row>
        {message ? (
          <Row
            label="Callback assistant"
            help="After the message, an AI voice asks only whether the person wants a callback from the rep and books it on the rep's Today. Never a cell. It may go beyond what the message may say; accept that risk in Learn."
          >
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <Switch
                  label="Callback assistant"
                  checked={assistant && s.callback_voice_available}
                  disabled={!s.callback_voice_available}
                  onChange={setAssistant}
                />
                {!s.callback_voice_available ? <Badge tone="neutral">Not available yet</Badge> : null}
              </div>
              {assistant && s.callback_voice_available ? (
                <TextCheck
                  kind="instructions"
                  label="Assistant instructions"
                  value={instructions}
                  onChange={setInstructions}
                  result={insCheck}
                  onResult={setInsCheck}
                  rows={2}
                  placeholder="Tone and callback windows only, for example: warm and brief; suggest mornings 9 to 11 Central."
                />
              ) : null}
            </div>
          </Row>
        ) : null}
        <Row label="Treat VoIP numbers as cells" help="Off: VoIP numbers count as landlines.">
          <Switch label="Treat VoIP numbers as cells" checked={voip} onChange={setVoip} />
        </Row>
      </ul>
      <footer className="flex flex-wrap items-center justify-end gap-2 border-hairline border-t px-4 py-3">
        <div className="mr-auto flex flex-col gap-1">
          <InlineError error={save.error ?? ackThenSave.error} />
        </div>
        {dirty ? (
          <Button
            variant="ghost"
            onClick={() => {
              setInstructions(s.callback_instructions ?? "");
              setAssistant(s.callback_assistant);
              setVoip(s.voip_as_cell);
            }}
          >
            Discard changes
          </Button>
        ) : null}
        <Button
          variant="primary"
          disabled={!dirty}
          busy={save.isPending || ackThenSave.isPending}
          onClick={() => (needed.length > 0 ? setAckOpen(true) : save.mutate(patch))}
        >
          Save
        </Button>
      </footer>
      <Dialog open={ackOpen} onClose={() => setAckOpen(false)} title="Accept the new risks" size="lg">
        {learn.data ? (
          <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto pr-1">
            <Acknowledge
              learn={learn.data}
              mode={s.second_answer_mode}
              assistant={assistant}
              cells={s.cells_in_batches}
              acked={s.acked_items}
              busy={ackThenSave.isPending}
              onConfirm={(items) => ackThenSave.mutate(items)}
            />
            <InlineError error={ackThenSave.error} />
          </div>
        ) : (
          <Loading />
        )}
      </Dialog>
    </Card>
  );
}

// Reps and limits -----------------------------------------------------------------------------

function LimitsTab({ s }: { s: PdialAdmin }) {
  const qc = useQueryClient();
  const grant = useMutation({
    mutationFn: (v: { id: string; on: boolean; limit: number | null }) => pdial.grant(v.id, v.on, v.limit),
    onSuccess: (v) => qc.setQueryData(pdialKeys.admin, v),
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  const [limits, setLimits] = useState<Record<string, string>>({});
  return (
    <div className="flex flex-col gap-4">
      <Card title="Company limits">
        <CompanyLimits limits={s.limits} />
      </Card>
      <Card title="Today">
        <RepLimits reps={s.reps} limits={s.limits} />
      </Card>
      <Card title="Who may dial in parallel">
        <Table aria-label="Grants">
          <THead>
            <tr>
              <Th>Rep</Th>
              <Th>Allowed</Th>
              <Th>Daily limit</Th>
              <Th>Status</Th>
            </tr>
          </THead>
          <TBody>
            {s.reps.map((r) => {
              const b = availabilityBadge(r.availability);
              const v = limits[r.user_id] ?? (r.daily_limit === null ? "" : String(r.daily_limit));
              return (
                <Tr key={r.user_id}>
                  <Td>
                    <div className="flex flex-col">
                      <span>{r.name}</span>
                      <span className="text-caption text-ink-3">{r.email}</span>
                    </div>
                  </Td>
                  <Td>
                    <Switch
                      label={`Parallel dialing for ${r.name}`}
                      checked={r.granted}
                      onChange={(on) =>
                        grant.mutate({ id: r.user_id, on, limit: v.trim() ? Number(v) : null })
                      }
                    />
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      <Input
                        aria-label={`Daily limit for ${r.name}`}
                        inputMode="numeric"
                        className="w-24 tnum"
                        placeholder={
                          s.limits.rep_default_limit === null ? "None" : String(s.limits.rep_default_limit)
                        }
                        value={v}
                        disabled={!r.granted}
                        onChange={(e) => setLimits((m) => ({ ...m, [r.user_id]: e.target.value }))}
                        onBlur={() => {
                          if (r.granted && v !== (r.daily_limit === null ? "" : String(r.daily_limit)))
                            grant.mutate({
                              id: r.user_id,
                              on: true,
                              limit: v.trim() ? Number(v) : null,
                            });
                        }}
                      />
                    </div>
                  </Td>
                  <Td>
                    <span title={r.availability.message}>
                      <Badge tone={b.tone}>{b.label}</Badge>
                    </span>
                  </Td>
                </Tr>
              );
            })}
          </TBody>
        </Table>
      </Card>
      <PolicyCard key={JSON.stringify(s.policy)} s={s} />
    </div>
  );
}

function num(v: string): number | null {
  const t = v.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function PolicyCard({ s }: { s: PdialAdmin }) {
  const save = useSave();
  const [p, setP] = useState<PdialPolicy>(s.policy);
  const set = <K extends keyof PdialPolicy>(k: K, v: PdialPolicy[K]) => setP((x) => ({ ...x, [k]: v }));
  const field = (label: string, k: keyof PdialPolicy, unit: string, help?: string) => (
    <Row label={label} {...(help ? { help } : {})}>
      <div className="flex items-center gap-2">
        <Input
          aria-label={label}
          inputMode="decimal"
          className="w-24 tnum"
          value={p[k] === null ? "" : String(p[k])}
          onChange={(e) => set(k, num(e.target.value) as never)}
        />
        <span className="text-ui text-ink-2">{unit}</span>
      </div>
    </Row>
  );
  const dirty = JSON.stringify(p) !== JSON.stringify(s.policy);
  return (
    <Card title="Caps and defaults">
      <ul className="divide-y divide-hairline">
        {field("Most lines per rep", "max_lines", "lines (1 to 5)")}
        {field("Rep daily limit", "rep_daily_limit", "parallel dials, unless set per rep")}
        {field("Company daily limit", "company_daily_limit", "parallel dials")}
        {field("Abandonment target", "abandon_target_pct", "%")}
        {field("Reduce lines above", "reduce_above_pct", "%")}
        {field(
          "One line above",
          "force_single_above_pct",
          "%",
          "Back to more lines once under the recovery rate.",
        )}
        {field("Recovery under", "recover_below_pct", "%")}
        {field("Ring for", "ring_timeout_secs", "seconds")}
        {field("Number daily cap", "daily_cap_per_number", "dials per pool number")}
      </ul>
      <footer className="flex items-center justify-end gap-2 border-hairline border-t px-4 py-3">
        <Button
          variant="primary"
          disabled={!dirty}
          busy={save.isPending}
          onClick={() => save.mutate({ policy: p })}
        >
          Save
        </Button>
      </footer>
    </Card>
  );
}

// Rules ---------------------------------------------------------------------------------------

function RulesTab({ s }: { s: PdialAdmin }) {
  const qc = useQueryClient();
  const put = useMutation({
    mutationFn: (r: StateRule) => pdial.putStateRule(r),
    onSuccess: (v) => qc.setQueryData(pdialKeys.admin, v),
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  const del = useMutation({
    mutationFn: (st: string) => pdial.deleteStateRule(st),
    onSuccess: (v) => qc.setQueryData(pdialKeys.admin, v),
    onError: (e) => toast(errorMessage(e), "danger"),
  });
  const [add, setAdd] = useState("");
  const c = s.policy.cooldowns;
  return (
    <div className="flex flex-col gap-4">
      <Card
        title="States"
        actions={
          <div className="flex items-center gap-2">
            <label htmlFor="pdial-add-state" className="text-ui text-ink-2">
              State
            </label>
            <Input
              id="pdial-add-state"
              aria-label="State to add"
              placeholder="TX"
              className="w-16 uppercase"
              maxLength={2}
              value={add}
              onChange={(e) => setAdd(e.target.value.toUpperCase())}
            />
            <Button
              size="sm"
              disabled={!/^[A-Z]{2}$/.test(add)}
              onClick={() => {
                put.mutate({
                  state: add,
                  single_line: true,
                  no_message: false,
                  max_attempts_24h: null,
                  no_sunday: false,
                  no_call_dates: [],
                });
                setAdd("");
              }}
            >
              Add state
            </Button>
          </div>
        }
      >
        <Table aria-label="State rules">
          <THead>
            <tr>
              <Th>State</Th>
              <Th>One line</Th>
              <Th>No message</Th>
              <Th>Attempts in 24 h</Th>
              <Th>No Sundays</Th>
              <Th>No-call dates</Th>
              <Th>
                <span className="sr-only">Remove</span>
              </Th>
            </tr>
          </THead>
          <TBody>
            {s.state_rules.map((r) => (
              <Tr key={r.state}>
                <Td className="font-medium">{r.state}</Td>
                <Td>
                  <Switch
                    label={`${r.state} one line`}
                    checked={r.single_line}
                    onChange={(v) => put.mutate({ ...r, single_line: v })}
                  />
                </Td>
                <Td>
                  <Switch
                    label={`${r.state} no message`}
                    checked={r.no_message}
                    onChange={(v) => put.mutate({ ...r, no_message: v })}
                  />
                </Td>
                <Td className="tnum">{r.max_attempts_24h ?? "No limit"}</Td>
                <Td>
                  <Switch
                    label={`${r.state} no Sundays`}
                    checked={r.no_sunday}
                    onChange={(v) => put.mutate({ ...r, no_sunday: v })}
                  />
                </Td>
                <Td>
                  <NoCallDates rule={r} onSave={(dates) => put.mutate({ ...r, no_call_dates: dates })} />
                </Td>
                <Td>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Remove the ${r.state} rule`}
                    onClick={() => del.mutate(r.state)}
                  >
                    Remove
                  </Button>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
        <p className="px-4 py-3 text-caption text-ink-3">
          Everyone is called between 8 am and 8 pm in their own time zone, inside the company's calling hours.
          No-call dates are each year's state holidays (Alabama and Louisiana); enter them as YYYY-MM-DD,
          separated by commas.
        </p>
        <div className="px-4 pb-3">
          <InlineError error={put.error ?? del.error} />
        </div>
      </Card>
      <Card title="Cool-downs">
        <ul className="divide-y divide-hairline">
          <Row label="Cancelled line">{c.cancelled_mins / 60} hours (the number)</Row>
          <Row label="No answer">{c.no_answer_mins / 60} hours, or the list's retry rules</Row>
          <Row label="Machine">Next business day, when calling hours open in the contact's time zone</Row>
          <Row label="Busy or failed">{c.busy_mins / 60} hour; three in a row flags the number</Row>
          <Row label="Answered or abandoned">
            Never again in the same session; abandoned rests {c.abandoned_mins / 60} hours
          </Row>
        </ul>
      </Card>
      <Card title="Number pool">
        <Table aria-label="Number pool">
          <THead>
            <tr>
              <Th>Number</Th>
              <Th>Pool</Th>
              <Th numeric>Dials today</Th>
              <Th numeric>Answered (7 days)</Th>
              <Th>Status</Th>
            </tr>
          </THead>
          <TBody>
            {s.pool.map((n) => (
              <Tr key={n.id}>
                <Td className="tnum">{fmtPhone(n.e164)}</Td>
                <Td>{n.brand_name ?? <span className="text-ink-3">Not in a pool</span>}</Td>
                <Td numeric className="tnum">
                  {n.pool_brand_id ? `${n.dials_today} of ${n.daily_cap}` : "None"}
                </Td>
                <Td numeric className="tnum">
                  {n.answer_rate_7d === null ? "None" : pct(n.answer_rate_7d, 0)}
                </Td>
                <Td>
                  {n.rested_until ? (
                    <span title={n.rest_reason ?? ""}>
                      <Badge tone="warn">Resting until {fmtDateTime(n.rested_until)}</Badge>
                    </span>
                  ) : n.pool_brand_id ? (
                    <Badge tone="ok">In use</Badge>
                  ) : null}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      </Card>
    </div>
  );
}

// Meter and records ---------------------------------------------------------------------------

function RecordsTab({ s }: { s: PdialAdmin }) {
  const a = useQuery(attemptsQuery);
  return (
    <div className="flex flex-col gap-4">
      <Card title="Abandonment meter (30 days)">
        <Meters meters={s.meters} />
      </Card>
      <Card
        title="Records"
        actions={
          <a
            className="text-accent-ink text-ui hover:underline"
            href="/v1/admin/pdial/attempts?format=csv"
            download
          >
            Export CSV
          </a>
        }
      >
        {a.isPending ? (
          <Loading />
        ) : a.isError ? (
          <ErrorState error={a.error} onRetry={() => a.refetch()} />
        ) : (
          <Table aria-label="Attempts">
            <THead>
              <tr>
                <Th>Dialed</Th>
                <Th>Rep</Th>
                <Th>Contact</Th>
                <Th>Number</Th>
                <Th>Result</Th>
                <Th numeric>Cancel</Th>
              </tr>
            </THead>
            <TBody>
              {a.data.items.map((r) => (
                <Tr key={r.id}>
                  <Td className="tnum">{fmtDateTime(r.dialed_at)}</Td>
                  <Td>{r.rep_name}</Td>
                  <Td>
                    {r.contact_name}
                    <span className="block text-caption text-ink-3">{r.company}</span>
                  </Td>
                  <Td>
                    <PhoneNumber
                      e164={r.phone}
                      type={r.line_type}
                      detail={`Stored as ${r.line_type}`}
                      className="whitespace-nowrap"
                    />
                  </Td>
                  <Td>{r.outcome?.replace(/_/g, " ") ?? "Ringing"}</Td>
                  <Td numeric className="tnum">
                    {r.cancel_latency_ms === null ? "" : `${r.cancel_latency_ms} ms`}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
        <p className="px-4 py-3 text-caption text-ink-3">Kept for 5 years.</p>
      </Card>
    </div>
  );
}

function NoCallDates({ rule, onSave }: { rule: StateRule; onSave: (dates: string[]) => void }) {
  const [text, setText] = useState(rule.no_call_dates.join(", "));
  const dates = text
    .split(/[,\s]+/)
    .map((d) => d.trim())
    .filter(Boolean);
  const valid = dates.every((d) => /^\d{4}-\d{2}-\d{2}$/.test(d));
  const changed = dates.join(",") !== rule.no_call_dates.join(",");
  return (
    <div className="flex items-center gap-2">
      <Input
        aria-label={`${rule.state} no-call dates`}
        className="w-48"
        placeholder="None"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {changed ? (
        <Button
          size="sm"
          disabled={!valid}
          onClick={() => onSave(dates)}
          aria-label={`Save ${rule.state} no-call dates`}
        >
          Save
        </Button>
      ) : null}
    </div>
  );
}
